/* eslint-disable indent */
import { PropTypes } from '../propTypes/index.js';
import type { ReactDescT } from '../propTypes/types.js';

// recursive type where the key is a string and the value it's the either a string or a recursive type
type DataTestId = {
  [key: string]: string | DataTestId;
};
// typescript typeguard to check if the value is a string
const isString = (value: string | DataTestId): value is string => typeof value === 'string';
// typescript typeguard to check if the value is a DataTestId
const isDataTestId = (value: string | DataTestId): value is DataTestId => typeof value === 'object';

type PropsPerDataTestIdPropTypes = {
  [key: string]: ReactDescT['shape'];
};
// implementation of a helper function that retrieves the nested values of the input object
// and returns an array of strings, each string is the value of a leaf of the input object
/**
 *
 * @param {object} obj - object in which the values rapresent the keys of the output object
 * @returns {string[]} an array of strings, each string is the value of a leaf of the input object
 * @example
 * const obj = {
 *   WHATEVER: 'data-testid',
 *   NESTED:{
 *     WHATEVER2: 'data-testid-2',
 *     WHATEVER3: 'data-testid-3',
 *   }
 * }
 * const leafValues = getLeafValues(obj);
 * leafValues === ['data-testid', 'data-testid-2', 'data-testid-3']
 */
const getLeafValues = (obj: DataTestId): string[] => {
  const keys = Object.keys(obj);
  const leafValues = keys.reduce((acc, key) => {
    const value = obj[key];
    if (isString(value)) {
      acc.push(`${value}`);
    } else if (isDataTestId(value)) {
      acc.push(...getLeafValues(value));
    }
    return acc;
  }, [] as string[]);
  return leafValues;
};

/**
 * @param {object} dataTestIdObj - object in which the values rapresent the keys of the output object
 * @returns {object} output - an object in which for each leaf value of the input object there is a key with aria-* and data-* props.shape definition
 * @example
 * const dataTestIdObj = {
 *   WHATEVER: 'data-testid',
 *   NESTED:{
 *     WHATEVER2: 'data-testid-2',
 *     WHATEVER3: 'data-testid-3',
 *   }
 * }
 *
 * const propsPerDataTestIdPropTypes = getPropsPerDatatestIdPropTypes(dataTestIdObj);
 * // propsPerDataTestIdPropTypes will be
 * {
 *   'data-testid': PropTypes.shape({
 *      'aria-*': PropTypes.string,
 *      'data-*': PropTypes.string,
 *   }),
 *   'data-testid-2': PropTypes.shape({
 *      'aria-*': PropTypes.string,
 *      'data-*': PropTypes.string,
 *   })
 *   'data-testid-3': PropTypes.shape({
 *      'aria-*': PropTypes.string,
 *      'data-*': PropTypes.string,
 *   })
 * }
 * @fires getLeafValues
 */
export const getPropsPerDatatestIdPropTypes = (dataTestIdObj: DataTestId): PropsPerDataTestIdPropTypes => {
  const leafValues = getLeafValues(dataTestIdObj);
  const propsPerDataTestIdPropTypes = {} as PropsPerDataTestIdPropTypes;
  leafValues.forEach((leafValue) => {
    propsPerDataTestIdPropTypes[leafValue] = PropTypes.shape({
      'aria-*': PropTypes.string.description('Aria related properties'),
      'data-*': PropTypes.string.description('Any data property to attach to the root container'),
      'on-*': PropTypes.func.description('Any supported React event'),
      'all HTML attributes': PropTypes.any.description(
        'HTML attributes such as id, className, autoComplete, href and so on',
      ),
    })
      .description(`an object containing the definitions for ${leafValue} aria and data props`)
      .dataTestId();
  });
  return propsPerDataTestIdPropTypes;
};
